import React, { Component, useEffect, createRef, useRef, useState } from "react";
import { AutocompleteRenderGroupParams } from "@mui/material";
import { Button, IconButton, TextField } from "@mui/material";
import { DeleteOutlined, EditOutlined } from "@mui/icons-material";
import { AutocompleteRenderOptionState } from "@mui/material/Autocomplete/Autocomplete";
import { uniqueId } from "lodash";
import { getNameOfAConcatString } from "utils";

interface AutocompleteCollapsableGroupProps extends AutocompleteRenderGroupParams {
  inputValue?: string;
  actions: { [key: string]: React.Dispatch<React.SetStateAction<any>> };
  multiple?: boolean;
  hideHeader?: boolean;
  id?: string;
}

interface AutocompleteCollapsableGroupState {
  open: boolean;
  clicked: boolean;
}

// hook must be defined in top class, so use class component instead
class AutocompleteCollapsableGroup extends Component<
  AutocompleteCollapsableGroupProps,
  AutocompleteCollapsableGroupState
> {
  constructor(params: AutocompleteCollapsableGroupProps) {
    super(params);
  }

  render() {
    // const { open } = this.state;
    const { group, children, actions, multiple = false, hideHeader = false, id } = this.props;
    return [
      <div
        key={group}
        style={{
          display: hideHeader ? "none" : "flex",
          alignItems: "center",
          fontSize: 11,
          fontWeight: 600,
          color: "#333",
          padding: "0 12px",
          marginTop: 12,
        }}
      >
        {group}
      </div>,
      <div key={group + "-children"}>
        {/*for other group, the first item is empty in order to let the component render other group!*/}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            fontSize: 11,
            padding: "0 12px",
            flexDirection: group === "Other" ? "column" : "row",
          }}
        >
          {(children as any[])
            ?.filter((x) => x?.props?.children?.props?.option?.value)
            .map((x) => {
              return {
                ...x,
                props: {
                  ...x.props,
                  onClick: (event: any) => {
                    if (
                      (event?.relatedTarget as HTMLElement)?.className?.includes?.("block-close") ||
                      (event?.target as HTMLElement)?.parentElement?.className?.includes?.(
                        "block-close"
                      ) ||
                      (event?.target as HTMLElement)?.className?.includes?.("block-close")
                    ) {
                      return event;
                    }
                    return x?.props?.onClick(event);
                  },
                },
              };
            })}
        </div>
        {group === "Other" && (multiple || (!multiple && (children as any[])?.length < 2)) && (
          <div style={{ margin: "8px 0" }}>
            <AddOtherOptionComponent
              actions={actions}
              id={id}
            />
          </div>
        )}
      </div>,
    ];
  }
}

export const OtherComponent = (props: {
  actions: { [key: string]: React.Dispatch<React.SetStateAction<any>> };
  defaultValue?: string;
  type?: string;
  multiple?: boolean;
  prefixNameWithOther?: boolean;
  validWithPrefix?: boolean;
}) => {
  const ref = useRef<HTMLInputElement>();
  const {
    type = "create",
    actions,
    defaultValue = "",
    multiple = false,
    prefixNameWithOther = false,
    validWithPrefix = false,
  } = props;
  const { setOptions, setPortal, setValue } = actions;

  const [helpText, setHelpText] = useState("");

  const [focus, setFocus] = useState(false);

  const onCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setPortal((prv: any) => {
      prv?.callback?.(false);
      return null;
    });
  };

  const validate = () => {
    const newValue = ref.current?.value?.trim() || "";
    // case 1: can not add empty value
    if (!newValue) {
      setHelpText("Please provide a valid option");
      return false;
    }
    // case 2: maximum length 65
    if (newValue.length > 65) {
      setHelpText("Maximum character length is 65");
      return false;
    }
    // case 3: same option check
    let existed = false;
    setOptions((prv: any) => {
      if (
        prv.filter(
          (x: any) =>
            x?.type === "Other" && x?.name === (validWithPrefix ? "Other - " : "") + newValue
        ).length > 0
      ) {
        existed = true;
      }
      return prv;
    });
    if (existed && newValue !== getNameOfAConcatString(defaultValue)) {
      setHelpText("Another same option already existed");
      return false;
    }

    return true;
  };

  const onSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.nativeEvent.stopPropagation();
    if (!validate()) return;
    const newValue = {
      type: "Other",
      name: `${prefixNameWithOther ? "Other - " : ""}${ref.current?.value?.trim()}`,
      value: `Other%-%${ref.current?.value}`,
    };
    if (type === "create") {
      setOptions((prv: any) => [...prv, newValue]);
    } else {
      setOptions((prv: any) => [...prv.filter((y: any) => y.value !== defaultValue), newValue]);
    }
    if (multiple) {
      setValue((prv: any) => [...prv.filter((x: any) => x.value !== defaultValue), newValue]);
    } else {
      setValue(newValue);
    }
    setPortal((prv: any) => {
      prv?.callback?.(false);
      return null;
    });
    if (ref.current) ref.current.value = "";
  };

  const onClick = () => {
    ref.current?.focus();
  };

  const onFocus = () => {
    setFocus(true);
  };

  const onBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.currentTarget.className.includes("block-close")) return;
    setFocus(false);
  };

  useEffect(() => {
    if (type === "update") {
      ref.current?.focus();
    }
    ref.current?.setSelectionRange(ref.current?.value.length || 0, ref.current?.value.length || 0);
  }, []);

  return (
    <TextField
      onFocus={onFocus}
      onBlur={onBlur}
      onClick={onClick}
      error={!!helpText}
      helperText={helpText}
      inputRef={ref}
      variant={"outlined"}
      size={"small"}
      style={{ width: "100%" }}
      defaultValue={getNameOfAConcatString(defaultValue)}
      multiline={true}
      maxRows={3}
      placeholder={"Don't find the value you want to select? Type here and submit"}
      inputProps={{
        className: "block-close",
      }}
      InputProps={{
        style: {
          paddingBottom: 24,
        },
        endAdornment: (
          <>
            {type === "update" && (
              <Button
                size={"small"}
                variant={"text"}
                onClick={onCancel}
                color={"primary"}
                className={"block-close"}
                style={{
                  textTransform: "none",
                  position: "absolute",
                  bottom: 0,
                  right: 64,
                }}
                disableElevation={true}
                disableRipple={true}
              >
                Cancel
              </Button>
            )}
            <Button
              variant={"text"}
              size={"small"}
              onClick={onSubmit}
              className={"block-close"}
              color={"primary"}
              style={{
                textTransform: "none",
                position: "absolute",
                bottom: 0,
                right: 0,
                display: focus ? "inherit" : "none",
              }}
              disableElevation={true}
              disableRipple={true}
            >
              Submit
            </Button>
          </>
        ),
      }}
    />
  );
};

interface OptionComponentProps<T> {
  option: T;
  state: AutocompleteRenderOptionState;
  actions: { [key: string]: React.Dispatch<React.SetStateAction<any>> };
  multiple?: boolean;
}

export class OptionComponent<
  T extends { name: string; value: string; type: string },
> extends Component<OptionComponentProps<T>, { edit: boolean }> {
  private renderRef: React.RefObject<HTMLDivElement>;
  constructor(props: OptionComponentProps<T>) {
    super(props);
    this.state = { edit: false };
    this.renderRef = createRef();
  }
  render() {
    const { state, option, actions, multiple = false } = this.props;
    const { setOptions, setValue, setPortal, setInputValue } = actions;
    const { selected } = state;
    const { edit } = this.state;

    const onEdit = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      this.setState({ edit: true });
      setPortal((prv: any) => {
        prv?.callback?.(false);
        return {
          id: option.value,
          option,
          callback: (edit: boolean) => {
            this.setState({ edit });
          },
        };
      });
    };

    const onDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setOptions?.((prv: T[]) => prv.filter((x) => x.value !== option.value));
      if (selected) {
        if (multiple) {
          setValue((prv: T[]) => prv.filter((x) => x.value !== option.value));
        } else {
          setValue(null);
        }
        setInputValue("");
      }
      this.setState({ edit: false });
    };

    if (option.value) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              width: "100%",
            }}
            className={"hover-show-container"}
          >
            <div
              id={option.value}
              style={{ width: edit ? "100%" : "0", margin: 0 }}
            />
            {!edit && (
              <>
                {/* {multiple && <Checkbox color={"primary"} checked={selected} />} */}
                <p
                  style={{
                    wordBreak: "break-all",
                    // marginLeft: !multiple ? 24 : 0,
                    padding: "3px 6px",
                    fontSize: 11,
                    borderRadius: 3,
                    backgroundColor: selected ? "#00A0D1" : "#E6F1FC",
                    color: selected ? "#E6F1FC" : "#00A0D1",
                  }}
                >
                  {option.name}
                </p>
                {option.type === "Other" && (
                  <div
                    className={"hover-show"}
                    style={{ background: "#fff" }}
                  >
                    <IconButton
                      size={"small"}
                      onClick={onEdit}
                      className={"block-svg block-close"}
                    >
                      <EditOutlined style={{ color: "#007AA3" }} />
                    </IconButton>
                    <IconButton
                      size={"small"}
                      onClick={onDelete}
                      color={"error"}
                      className={"block-svg block-close"}
                    >
                      <DeleteOutlined />
                    </IconButton>
                  </div>
                )}
              </>
            )}
          </div>

          {/* {selected && !edit && !multiple ? (
            <Done style={{ color: "#007AA3" }} />
          ) : null} */}
        </div>
      );
    }

    return null;
  }
}

export class AddOtherOptionComponent<
  T extends { name: string; value: string; type: string },
> extends Component<
  {
    actions: { [key: string]: React.Dispatch<React.SetStateAction<any>> };
    id?: string;
  },
  { edit: boolean }
> {
  constructor(props: OptionComponentProps<T>) {
    super(props);
    this.state = { edit: false };
  }
  render() {
    const id = this.props.id || uniqueId("add-new-other");
    // const { actions } = this.props;
    // const { setPortal } = actions;
    // const { edit } = this.state;

    // const onEdit = () => {
    //   this.setState({ edit: true });
    //   setPortal((prv: any) => {
    //     prv?.callback(false);
    //     return {
    //       id,
    //       option: null,
    //       callback: (edit: boolean) => {
    //         this.setState({ edit });
    //       },
    //     };
    //   });
    // };

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <div
          id={id}
          style={{ width: "90%" }}
        />
      </div>
    );
  }
}

export default AutocompleteCollapsableGroup;
